import React, { type ReactElement, useMemo, useState } from "react";

import {
  exportDATEVXML,
  exportInvoicesCSV,
  exportLineItemsCSV,
  exportPDFs,
  exportSuppliersCSV,
} from "@/apis/custom/customRoutes";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

import { usePermissions } from "@/hooks/usePermissions";
import { Exports } from "@/lib/constants/exports";
import {
  InvoiceProcessingSteps,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";
import { useGetAllDatevTokens } from "@/queries/datev-tokens/get-all";
import ExportAlertDialog from "./ExportAlertDialog"; // Make sure the path is correct
import { DatevExportButton } from "./datev-export-button";

const ExportButtonNew = ({
  selectedInvoiceRows,
  allInvoiceIds,
  buttonStyle,
  onSuccess,
  options: _options = [
    Exports.AutomaticDatev,
    Exports.Datev,
    Exports.LineItems,
    Exports.Invoices,
    Exports.Suppliers,
    Exports.Pdf,
  ],
}: {
  options?: Exports[];
  selectedInvoiceRows: any[];
  allInvoiceIds: string[];
  buttonStyle?: any;
  onSuccess?: any;
}) => {
  const [isExportAlertOpen, setIsExportAlertOpen] = useState(false);
  const [exportDialog, setExportDialog] = useState({
    title: "",
    description: "",
    onConfirm: () => {},
    disallowExport: false,
  });

  const { isSuperAdmin } = usePermissions();
  const { tokens } = useGetAllDatevTokens();

  const token = tokens?.[0];

  const selectedInvoiceIds =
    (selectedInvoiceRows && selectedInvoiceRows?.length) > 0
      ? selectedInvoiceRows.map((row) => row.original?.invoice.invoiceId)
      : [];

  const { t } = useTranslation();

  const showExportConfirmation = (
    title: string,
    description: string,
    onConfirm: any,
    disallowExport: boolean,
  ) => {
    setExportDialog({ title, description, onConfirm, disallowExport });
    setIsExportAlertOpen(true);
  };

  const handleExportLineItemsClick = () => {
    if (selectedInvoiceIds.length > 0) {
      exportLineItemsCSV(selectedInvoiceIds);
    } else {
      showExportConfirmation(
        "Wollen Sie wirklich alle Einzelposten exportieren?",
        "Wenn keine Rechnungen ausgewählt sind, werden die Einzelposten für alle Rechnungen auf einmal exportiert. Sonst werden nur die Einzelposten für die ausgewählten Rechnungen exportiert.",
        () => {
          exportLineItemsCSV(allInvoiceIds);
          setIsExportAlertOpen(false);
        },
        false,
      );
    }
  };

  const handleExportInvoicesClick = () => {
    if (selectedInvoiceIds.length > 0) {
      exportInvoicesCSV(selectedInvoiceIds);
    } else {
      showExportConfirmation(
        "Wollen Sie wirklich alle Rechnungen exportieren?",
        "Wenn keine Rechnungen ausgewählt sind, werden alle auf einmal exportiert. Sonst werden nur die ausgewählten Rechnungen exportiert.",
        () => {
          exportInvoicesCSV(allInvoiceIds);
          setIsExportAlertOpen(false);
        },
        false,
      );
    }
  };

  const handleExportPdfsClick = () => {
    if (selectedInvoiceIds.length > 0) {
      exportPDFs(selectedInvoiceIds);
    } else {
      showExportConfirmation(
        "Wollen Sie wirklich alle Rechnungen exportieren?",
        "Wenn keine Rechnungen ausgewählt sind, werden alle auf einmal exportiert. Sonst werden nur die ausgewählten Rechnungen exportiert.",
        () => {
          exportPDFs(allInvoiceIds);
          setIsExportAlertOpen(false);
        },
        false,
      );
    }
  };

  const hasUnreviewed = useMemo(() => {
    return (selectedInvoiceRows as any).some(
      (row: any) =>
        !InvoiceProcessingSteps.IS_REVIEWED.includes(
          row.original?.invoice.status,
        ),
    );
  }, [selectedInvoiceRows]);

  const hasExported = useMemo(() => {
    return (selectedInvoiceRows as any).some(
      (row: any) => row.original?.invoice.status === InvoiceStatus.EXPORTED,
    );
  }, [selectedInvoiceRows]);

  const handleDatevExportClick = () => {
    if (hasUnreviewed) {
      showExportConfirmation(
        "DATEV Export",
        "Auswahl enthält Rechnungen mit Status Zu Prüfen. Es können nur Rechnungen im Status Fertig exportiert werden. Bitte überprüfen Sie die Auswahl.",
        () => setIsExportAlertOpen(false),
        true,
      );
      return;
    } else if (hasExported) {
      showExportConfirmation(
        "DATEV Export",
        "Auswahl enthält Rechnungen mit Status Exportiert. Wollen Sie diese nochmal exportieren?",
        () => {
          exportDATEVXML(selectedInvoiceIds)
            .then((successMessage) => {
              console.log(successMessage);
              onSuccess(selectedInvoiceIds);
            })
            .catch((errorMessage) => {
              console.error(errorMessage);
            });
          setIsExportAlertOpen(false);
        },
        false,
      );
      return;
    }

    if (selectedInvoiceIds.length > 0) {
      exportDATEVXML(selectedInvoiceIds)
        .then((successMessage) => {
          console.log(successMessage);
          onSuccess(selectedInvoiceIds);
        })
        .catch((errorMessage) => {
          console.error(errorMessage);
        });
    } else {
      showExportConfirmation(
        "Wollen Sie wirklich alle Rechnungen exportieren?",
        "Wenn keine Rechnungen ausgewählt sind, werden alle auf einmal exportiert. Sonst werden nur die ausgewählten Rechnungen exportiert.",
        () => {
          exportDATEVXML(allInvoiceIds)
            .then((successMessage) => {
              console.log(successMessage);
              onSuccess(selectedInvoiceIds);
            })
            .catch((errorMessage) => {
              console.error(errorMessage);
            });
          setIsExportAlertOpen(false);
        },
        false,
      );
    }
  };

  const options: Record<
    string,
    {
      label?: string;
      onSelect?: () => Promise<void> | void;
      Component?: ReactElement;
    }
  > = {
    // TODO: deprecated datev export button, we want to fully remove it but we need to know if users request it
    // [Exports.Datev]: {
    //   label: t("datev_export"),
    //   onSelect: handleDatevExportClick,
    // },
    [Exports.LineItems]: {
      label: t("line_items_export"),
      onSelect: handleExportLineItemsClick,
    },
    [Exports.Invoices]: {
      label: t("invoices_export"),
      onSelect: handleExportInvoicesClick,
    },
    [Exports.Suppliers]: {
      label: t("suppliers_export"),
      onSelect: exportSuppliersCSV,
    },
    [Exports.Pdf]: {
      label: t("pdf_export"),
      onSelect: handleExportPdfsClick,
    },
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button {...buttonStyle}>{t("export")}</Button>
        </DropdownMenuTrigger>
        <DatevExportButton>
          {({ handlePress: handleDatevExport }) => (
            <DropdownMenuContent className="w-72" side="top" sideOffset={20}>
              <DropdownMenuLabel>Export Optionen</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {(token || isSuperAdmin) &&
                _options.includes(Exports.AutomaticDatev) && (
                  <DropdownMenuItem onSelect={handleDatevExport}>
                    {`${t("datev_buchungsdatenservice_bookings_export")}...`}
                  </DropdownMenuItem>
                )}
              {_options.map((option) => {
                const item = options[option];

                if (!item) {
                  return null;
                }

                return (
                  <DropdownMenuItem key={option} onSelect={item?.onSelect}>
                    {item?.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          )}
        </DatevExportButton>
      </DropdownMenu>
      <ExportAlertDialog
        isOpen={isExportAlertOpen}
        title={exportDialog.title}
        description={exportDialog.description}
        onConfirm={exportDialog.onConfirm}
        onCancel={() => setIsExportAlertOpen(false)}
        disallowExport={exportDialog.disallowExport}
      />
    </>
  );
};

export default ExportButtonNew;
